import { useLanguage } from '../i18n/useLanguage';

// Language toggle pill per Figma 120:2100 (English) and 120:2356 (Thai).
// Both states share the maroon pill chrome (#5c1d34 / 2px #ba7d7d / 100px
// radius / soft drop shadow). The only difference is which side the
// flag sits on — English: flag RIGHT, label LEFT; Thai: flag LEFT, label
// RIGHT.
//
// The flag and label are both absolutely positioned inside a
// fixed-width pill so that toggling language animates a smooth slide
// of the flag from one side to the other (and a matching slide of the
// label in the opposite direction). Using `left` + `right` numeric
// values on both states (rather than `auto`) is what lets the
// transition tween smoothly — `auto` can't be animated to a length.
export default function LanguageSwitcher() {
  const { lang, setLang } = useLanguage();
  const next = lang === 'th' ? 'en' : 'th';
  const isThai = lang === 'th';
  const label = isThai ? 'Thai' : 'English';
  const flagSrc = isThai ? '/images/flag-th.png' : '/images/flag-en.png';

  // Flag position: Thai pins it to the left edge with a small pad;
  // English pushes it to the right edge. The lg variants use bigger
  // numbers because the pill, flag and inner padding are all larger
  // on lg+ (mobile pill 120×40 / flag 28; lg pill 200×60 / flag 50).
  const flagPos = isThai
    ? 'left-[-3px] lg:left-[-5px]'
    : 'left-[67px] lg:left-[114px]';

  // Label position: opposite of the flag — when the flag is on the
  // right, the label fills the left half (left-3 right-10); when the
  // flag is on the left, the label fills the right half (left-10
  // right-3). Both `left` AND `right` get explicit values so the
  // transition can animate either property cleanly.
  const labelPos = isThai
    ? 'left-10 right-3 lg:left-[62px] lg:right-4'
    : 'left-3 right-10 lg:left-4 lg:right-[62px]';

  return (
    <button
      type="button"
      onClick={() => setLang(next)}
      aria-label={`Switch language to ${next.toUpperCase()}`}
      className="relative inline-flex h-8 w-[100px] items-center rounded-pill border-2 border-[#ba7d7d] bg-[#5c1d34] text-white drop-shadow-[0_2px_2.5px_rgba(0,0,0,0.25)] transition-[filter] hover:brightness-110 lg:h-[50px] lg:w-[160px]"
    >
      <img
        src={flagSrc}
        alt=""
        aria-hidden="true"
        className={`absolute top-1/2 block h-8 w-8 -translate-y-1/2 select-none rounded-full object-cover transition-[left] duration-300 ease-out lg:h-[50px] lg:w-[50px] ${flagPos}`}
        draggable={false}
      />
      <span
        className={`absolute top-1/2 -translate-y-1/2 whitespace-nowrap text-center font-display text-sm font-bold leading-none transition-[left,right] duration-300 ease-out lg:text-2xl ${labelPos}`}
      >
        {label}
      </span>
    </button>
  );
}
